import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";
import ProtectedRoute from "./components/ProtectedRoute";
import { initAnalytics, trackPageview } from "./lib/analytics";

import Landing from "./pages/Landing";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PanelistRegister from "./pages/PanelistRegister";
import ClientRegister from "./pages/ClientRegister";
import PanelistDashboard from "./pages/PanelistDashboard";
import ClientDashboard from "./pages/ClientDashboard";
import CampaignBuilder from "./pages/CampaignBuilder";
import CampaignDetail from "./pages/CampaignDetail";
import ExecutiveReport from "./pages/ExecutiveReport";
import MicroResearch from "./pages/MicroResearch";
import MicroResearchExit from "./pages/MicroResearchExit";
import PaymentSuccess from "./pages/PaymentSuccess";
import LiveObservation from "./pages/LiveObservation";
import CompareStudies from "./pages/CompareStudies";
import ResearchMemory from "./pages/ResearchMemory";
import Resources from "./pages/Resources";
import DashboardHelp from "./pages/DashboardHelp";
import AccountProfile from "./pages/AccountProfile";

function App() {
  useEffect(() => { initAnalytics(); }, []);
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <CurrencyWrapper>
            <RouterAnalytics />
            <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register/panelist" element={<PanelistRegister />} />
            <Route path="/register/client" element={<ClientRegister />} />

            {/* Public Knowledge Base — How It Works */}
            <Route path="/resources" element={<Resources />} />
            <Route path="/how-it-works" element={<Navigate to="/resources" replace />} />

            <Route path="/panelist" element={
              <ProtectedRoute role="panelist"><PanelistDashboard /></ProtectedRoute>
            } />

            <Route path="/client" element={
              <ProtectedRoute role="client"><ClientDashboard /></ProtectedRoute>
            } />
            <Route path="/client/campaigns/new" element={
              <ProtectedRoute role="client"><CampaignBuilder /></ProtectedRoute>
            } />
            <Route path="/client/campaigns/:campaignId" element={
              <ProtectedRoute role="client"><CampaignDetail /></ProtectedRoute>
            } />
            <Route path="/client/campaigns/:campaignId/report" element={
              <ProtectedRoute role="client"><ExecutiveReport /></ProtectedRoute>
            } />
            <Route path="/client/campaigns/:campaignId/observe" element={
              <ProtectedRoute role="client"><LiveObservation /></ProtectedRoute>
            } />
            <Route path="/client/compare" element={
              <ProtectedRoute role="client"><CompareStudies /></ProtectedRoute>
            } />
            <Route path="/client/memory" element={
              <ProtectedRoute role="client"><ResearchMemory /></ProtectedRoute>
            } />
            {/* Authenticated client Help & Documentation console */}
            <Route path="/dashboard/help" element={
              <ProtectedRoute role="client"><DashboardHelp /></ProtectedRoute>
            } />
            {/* Authenticated account profile — currency + identity */}
            <Route path="/account/profile" element={
              <ProtectedRoute><AccountProfile /></ProtectedRoute>
            } />
            {/* Public stakeholder observation room — auth via signed token in ?token= */}
            <Route path="/observe/:campaignId" element={<LiveObservation />} />
            <Route path="/client/payment-success" element={
              <ProtectedRoute role="client"><PaymentSuccess /></ProtectedRoute>
            } />

            {/* Public micro-research run page */}
            <Route path="/micro-research/run" element={<MicroResearch />} />
            <Route path="/micro-research/exit" element={<MicroResearchExit />} />

            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
          </CurrencyWrapper>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

// CurrencyWrapper consumes the AuthContext (must be a child) and forwards
// the authenticated user into the CurrencyProvider so it can hydrate from
// user.preferred_currency without an extra round-trip.
function CurrencyWrapper({ children }) {
  const { user } = useAuth();
  return <CurrencyProvider user={user}>{children}</CurrencyProvider>;
}

// Route-change page-view emitter. Sits under BrowserRouter so `useLocation`
// works, and fires a GA4 page_view whenever the SPA URL changes. GA itself
// only initialises on the production hostname (see lib/analytics.js).
function RouterAnalytics() {
  const location = useLocation();
  useEffect(() => {
    trackPageview(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);
  return null;
}

export default App;
